import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaidIcon from '@mui/icons-material/Paid';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionStepper = motion(Stepper);

const HowItWorksSection = React.forwardRef<HTMLDivElement>((props, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const steps = [
    {
      label: 'Create Your Account',
      description:
        'Sign up in less than 2 minutes with a simple registration process.',
      icon: <PersonAddIcon />,
    },
    {
      label: 'Find Eligible Lawsuits',
      description:
        'Browse or search for class actions that match your purchase history or experiences.',
      icon: <SearchIcon />,
    },
    {
      label: 'Join Class Actions',
      description:
        'Easily enroll in lawsuits with just a few clicks and minimal documentation.',
      icon: <AssignmentIcon />,
    },
    {
      label: 'Receive Case Updates',
      description:
        'Get notifications on important case developments and settlement progress.',
      icon: <NotificationsIcon />,
    },
    {
      label: 'Collect Your Settlement',
      description:
        'Receive compensation directly through your preferred payment method.',
      icon: <PaidIcon />,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.1 * index,
      },
    }),
  };

  const lineVariants = {
    hidden: { scaleX: 0, originX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        delay: 0.5, // Delay after steps have appeared
        duration: 0.4,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.default',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          sx={{ textAlign: 'center', mb: 6 }}
        >
          <MotionTypography
            variant="h4"
            fontWeight={700}
            sx={{ mb: 2 }}
            variants={itemVariants}
          >
            How It Works
          </MotionTypography>
          <MotionTypography
            variant="h6"
            sx={{ maxWidth: 800, mx: 'auto', color: 'text.secondary' }}
            variants={itemVariants}
          >
            ClassAct simplifies the entire process from finding eligible
            lawsuits to receiving compensation.
          </MotionTypography>
        </MotionBox>

        {isMobile ? (
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={containerVariants}
          >
            <MotionStepper orientation="vertical" variants={containerVariants}>
              {steps.map((step, index) => (
                <MotionBox
                  key={step.label}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.1 }}
                  variants={stepVariants}
                >
                  <Step active={true}>
                    <StepLabel
                      StepIconProps={{
                        icon: index + 1,
                      }}
                      StepIconComponent={({ icon }) => (
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 2,
                          }}
                        >
                          {steps[Number(icon) - 1]?.icon || icon}
                        </Box>
                      )}
                    >
                      <Typography variant="h6" fontWeight={600}>
                        {step.label}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        {step.description}
                      </Typography>
                    </StepContent>
                  </Step>
                </MotionBox>
              ))}
            </MotionStepper>
          </MotionBox>
        ) : (
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={containerVariants}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              position: 'relative',
              px: 4,
            }}
          >
            {/* Connecting line */}
            <MotionBox
              variants={lineVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              sx={{
                position: 'absolute',
                top: '40px',
                left: '60px',
                right: '60px',
                height: '2px',
                bgcolor: 'primary.main',
                zIndex: 0,
              }}
            />

            {steps.map((step, index) => (
              <MotionBox
                key={step.label}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={stepVariants}
                sx={{
                  width: '20%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                  px: 1,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 3,
                    mb: 2,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                    '& svg': {
                      fontSize: 40,
                    },
                  }}
                >
                  {step.icon}
                </Box>
                <Typography
                  variant="h6"
                  component="h3"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  {step.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </MotionBox>
            ))}
          </MotionBox>
        )}
      </Container>
    </Box>
  );
});

export default HowItWorksSection;
